/**
 * @file Composable for delaying post sorting after reactions
 * It is only called once when surface_reactions store is initialized
 *
 * When a user adds/updates/removes a reaction on a board sorted by reactions:
 * - On non-touchable devices: we defer the re-sort until they hover out to avoid jarring UX on desktop
 * - On touchable devices: we defer the re-sort for 1 second after the reaction is added/removed
 *
 * The accumulated reactions for delayed sorting is a frozen copy of the accumulated reactions by wish ID.
 * It is used by surface_post_sorter to sort posts based on reactions (except emoji).
 *
 * Screen reader announcements are generated when sorting completes, providing specific position changes
 * in the format: "Post title moved up 3 places. New position: 2."
 *
 * Assumes that post never changes section.
 */
import device from '@@/bits/device'
import { np__ } from '@@/bits/intl'
import { isSortingBy } from '@@/bits/surface_settings_helper'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { AccumulatedReactionData, AccumulatedReactions, Id } from '@@/types'
import { computed, ref } from 'vue'

const TOUCHABLE_DELAY_MS = 1000

function useDelayedReactionSorting(accumulatedReactionsByWishId: { value: AccumulatedReactions }) {
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfacePostsStore = useSurfacePostsStore()
  const screenReaderNotificationStore = useScreenReaderNotificationsStore()

  const shouldDeferSortingPostsByReactions = computed<boolean>(() => {
    return isSortingBy(surfaceWishArrangementStore.originalWishSortBy, 'reactions')
  })

  const postIdTrackedForDelayedReactionSorting = ref<Id | null>(null)
  const postReactionDelayedForSorting = ref<AccumulatedReactionData | null>(null)
  const oldPosition = ref<number | null>(null)

  const computePosition = (postId: Id): number | null => {
    // Not grouped
    if (surfaceWishArrangementStore.isGroupByNone) {
      const position = surfacePostsStore.allSortedPosts.findIndex((post) => post.id === postId)
      return position >= 0 ? position : null
    }

    // Grouped
    for (const key in surfacePostsStore.allSortedPostsByGroup) {
      const posts = surfacePostsStore.allSortedPostsByGroup[key]
      const position = posts?.findIndex((post) => post.id === postId) ?? -1
      if (position >= 0) return position
    }

    // Not found
    return null
  }

  const announcePositionChangeOnScreenReader = ({
    postSubject,
    oldPosition,
    newPosition,
  }: {
    postSubject: string | undefined | null
    oldPosition: number | null
    newPosition: number | null
  }) => {
    if (oldPosition == null || newPosition == null || oldPosition === newPosition) return

    let message: string
    const intlContext = 'Posts are reordered when user votes on it via reactions (e.g. likes, 5-star rating).'
    const currentPosition = newPosition + 1 // 1-based position

    if (newPosition > oldPosition) {
      const numPlaces = newPosition - oldPosition
      const params = { postSubject, numPlaces, currentPosition }
      message =
        postSubject == null
          ? np__(
              intlContext,
              'The post you reacted on moved down %{numPlaces} place. New position: %{currentPosition}.',
              'The post you reacted on moved down %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
          : np__(
              intlContext,
              '"%{postSubject}" moved down %{numPlaces} place. New position: %{currentPosition}.',
              '"%{postSubject}" moved down %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
    } else {
      const numPlaces = oldPosition - newPosition
      const params = { postSubject, numPlaces, currentPosition }
      message =
        postSubject == null
          ? np__(
              intlContext,
              'The post you reacted on moved up %{numPlaces} place. New position: %{currentPosition}.',
              'The post you reacted on moved up %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
          : np__(
              intlContext,
              '"%{postSubject}" moved up %{numPlaces} place. New position: %{currentPosition}.',
              '"%{postSubject}" moved up %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
    }
    screenReaderNotificationStore.addScreenReaderMessage(message)
  }

  let touchableDelayTimer: ReturnType<typeof setTimeout> | null = null
  const clearTouchableDelayTimer = (): void => {
    if (touchableDelayTimer != null) {
      clearTimeout(touchableDelayTimer)
      touchableDelayTimer = null
    }
  }

  const startTrackingPostForDelayedReactionSort = (postId: Id, frozenReaction: AccumulatedReactionData): void => {
    clearTouchableDelayTimer()

    postIdTrackedForDelayedReactionSorting.value = postId
    postReactionDelayedForSorting.value = frozenReaction
    oldPosition.value = computePosition(postId)

    if (device.touchable) {
      touchableDelayTimer = setTimeout(() => {
        stopTrackingPostForDelayedReactionSort(postId)
      }, TOUCHABLE_DELAY_MS)
    }
  }

  const stopTrackingPostForDelayedReactionSort = (postId: Id): void => {
    if (postIdTrackedForDelayedReactionSorting.value !== postId) return

    clearTouchableDelayTimer()
    postIdTrackedForDelayedReactionSorting.value = null
    postReactionDelayedForSorting.value = null

    // Announce after the new positions take effect
    announcePositionChangeOnScreenReader({
      oldPosition: oldPosition.value,
      newPosition: computePosition(postId),
      postSubject: surfacePostsStore.postEntitiesById[postId]?.subject,
    })

    // Clear oldPosition only after the screen reader message has been populated
    oldPosition.value = null
  }

  const accumulatedReactionsForDelayedSorting = computed<AccumulatedReactions>(() => {
    const reactions = { ...accumulatedReactionsByWishId.value }
    if (postIdTrackedForDelayedReactionSorting.value != null && postReactionDelayedForSorting.value != null) {
      reactions[postIdTrackedForDelayedReactionSorting.value] = postReactionDelayedForSorting.value
    }
    return reactions
  })

  return {
    shouldDeferSortingPostsByReactions,
    accumulatedReactionsForDelayedSorting,
    startTrackingPostForDelayedReactionSort,
    stopTrackingPostForDelayedReactionSort,
  }
}
export { useDelayedReactionSorting }
